import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Plus, Trash2, Edit2, Image as ImageIcon, Loader2, Share2, Printer, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getCountryName } from '../../utils/countries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { useDivision7ModalState } from '../../hooks/useDivision7ModalState';
import { useDivision7Filters } from '../shared/useDivision7Filters';
import { Division7FilterBar } from '../shared/Division7FilterBar';
import { ShareSection } from '../../features/shared/ShareSection';
import { AddAirBarrierProductWizard } from './AddAirBarrierProductWizard';
import { QRThumbnail, AirBarrierPrintView } from './AirBarrierPrintView';
import { useDownload } from '../../context/DownloadContext';
import { OrangeCheckbox, QRPreviewModal } from '../shared/WizardComponents';
import { generatePdfFromElement, generatePdfFromModalContent, buildPdfFilename } from '../../utils/pdfExport';

export const AirBarriersSection = ({
  canEditCSIItems, canManageProject, companyId, projectId,
  csiItem, project, user,
  shareCompanySearch, showCompanySuggestions, companySuggestions,
  highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown,
  setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [showShareModal, setShowShareModal] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, productName: '' });

  const csiItemId = csiItem?.id;

  const fetchProducts = useCallback(async () => {
    if (!projectId) return;
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}/air-barrier-products/${projectId}${params}`);
      setProducts(res.data);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setSelectedIds(new Set()); }, [products]);

  const handleDelete = (product) => {
    setConfirmState({
      open: true,
      title: 'Delete Product',
      description: `Are you sure you want to delete "${product.productNameModel}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setDeleting(product.id);
        try {
          await axios.delete(`${API}/air-barrier-products/${projectId}/${product.id}`);
          setProducts(prev => prev.filter(p => p.id !== product.id));
          toast.success('Product deleted');
        } catch {
          toast.error('Failed to delete product');
        } finally {
          setDeleting(null);
        }
        setConfirmState({ open: false });
      },
    });
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    if (count === 0) return;
    setConfirmState({
      open: true,
      title: 'Delete selected products?',
      description: `You are about to delete ${count} product(s). This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const res = await axios.post(`${API}/air-barrier-products/${projectId}/bulk-delete`, {
            ids: Array.from(selectedIds),
          });
          const deleted = res.data.deletedCount || 0;
          setProducts(prev => prev.filter(p => !selectedIds.has(p.id)));
          setSelectedIds(new Set());
          toast.success(`Deleted ${deleted} product(s).`);
        } catch {
          toast.error('Failed to delete products');
        } finally {
          setBulkDeleting(false);
        }
        setConfirmState({ open: false });
      },
    });
  };

  const handleEdit = (product) => {
    isUserActionRef.current = true;
    openModal('edit', product.id);
    setEditingProduct(product);
    setShowWizard(true);
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleWizardComplete = () => {
    isUserActionRef.current = true;
    fetchProducts();
    setShowWizard(false);
    setEditingProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleWizardClose = () => {
    isUserActionRef.current = true;
    setShowWizard(false);
    setEditingProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const [printProduct, setPrintProduct] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const [pdfProduct, setPdfProduct] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const pdfRef = useRef(null);

  const { urlModal, urlProductId, openModal, closeModal } = useDivision7ModalState('ab');
  const { filtered, filters, setFilter, clearFilters, filterOptions, schema, activeCount } = useDivision7Filters('air-barriers', products);
  const isUserActionRef = useRef(false);

  // URL → modal sync (restore on refresh / back-forward)
  useEffect(() => {
    if (loading || isUserActionRef.current) return;
    if (!urlModal) {
      if (printProduct) setPrintProduct(null);
      if (showWizard) { setShowWizard(false); setEditingProduct(null); }
      return;
    }
    if (urlModal === 'preview') {
      if (printProduct) return;
      const p = products.find(x => x.id === urlProductId);
      if (!p || p.status !== 'active') { closeModal(); return; }
      setPrintLoading(true);
      axios.get(`${API}/air-barrier-products-view/${p.id}`)
        .then(res => setPrintProduct(res.data))
        .catch(() => { toast.error('Failed to load product'); closeModal(); })
        .finally(() => setPrintLoading(false));
    } else if (urlModal === 'edit') {
      if (showWizard) return;
      const p = products.find(x => x.id === urlProductId);
      if (!p) { closeModal(); return; }
      setEditingProduct(p);
      setShowWizard(true);
    }
  }, [urlModal, urlProductId, loading]);

  const handlePrint = async (product) => {
    isUserActionRef.current = true;
    openModal('preview', product.id);
    setPrintLoading(true);
    try {
      const res = await axios.get(`${API}/air-barrier-products-view/${product.id}`);
      setPrintProduct(res.data);
    } catch { toast.error('Failed to load product for printing'); closeModal(); }
    finally { setPrintLoading(false); isUserActionRef.current = false; }
  };

  const handlePrintClose = () => {
    isUserActionRef.current = true;
    setPrintProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleListDownloadPdf = async (product) => {
    setPdfLoading(product.id);
    try {
      const res = await axios.get(`${API}/air-barrier-products-view/${product.id}`);
      setPdfProduct(res.data);
    } catch { toast.error('Failed to load product'); setPdfLoading(null); }
  };

  const handleModalDownloadPdf = async () => {
    if (!printProduct) return;
    try {
      await generatePdfFromModalContent('ab-print-content-area', buildPdfFilename(printProduct));
    } catch (err) {
      console.error('PDF modal download error:', err);
      toast.error('PDF download failed. Please try again.');
    }
  };

  useEffect(() => {
    if (!pdfProduct || !pdfRef.current) return;
    let cancelled = false;
    const gen = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled || !pdfRef.current) return;
      try {
        await generatePdfFromElement(pdfRef.current, buildPdfFilename(pdfProduct));
      } catch (err) {
        console.error('PDF list download error:', err);
        toast.error('PDF download failed. Please try again.');
      }
      setPdfProduct(null);
      setPdfLoading(null);
    };
    gen();
    return () => { cancelled = true; };
  }, [pdfProduct]);

  const triggerBrowserPrint = () => {
    const el = document.getElementById('ab-print-content-area');
    if (!el) return;
    const printWin = window.open('', '', 'width=800,height=900');
    if (!printWin) { toast.error('Pop-up blocked'); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map(n => n.outerHTML).join('');
    printWin.document.write(`<!DOCTYPE html><html><head><title>Print</title>${styles}</head><body class="bg-white p-4">${el.innerHTML}</body></html>`);
    printWin.document.close();
    setTimeout(() => { printWin.print(); printWin.close(); }, 800);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = products.length > 0 && selectedIds.size === products.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < products.length;

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="ab-products-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="air-barriers-section">
      <Division7FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} filterOptions={filterOptions} schema={schema} activeCount={activeCount} testIdPrefix="ab" />
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {canManageProject && (
            <div className="text-xs text-slate-600" data-testid="ab-share-info">
              Shared with: {csiItem?.assignedCompanyName ? (
                <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
              ) : (
                <span className="text-slate-400">N/A</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageProject && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting || selectedIds.size === 0}
              data-testid="ab-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete All{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
          {canEditCSIItems && (
            <Button
              size="sm"
              data-testid="ab-add-product-btn"
              onClick={() => { setEditingProduct(null); setShowWizard(true); }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create Product
            </Button>
          )}
          {canManageProject && csiItem && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShareModal(true)}
              data-testid="ab-share-btn"
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg" data-testid="ab-no-products-placeholder">
          <p>{products.length === 0 ? 'No products created yet' : 'No products match the current filters'}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="ab-products-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canManageProject && (
                  <th className="w-10 px-3 py-3">
                    <OrangeCheckbox
                      id="ab-select-all"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      testId="ab-select-all-checkbox"
                    />
                  </th>
                )}
                <th className="w-16 px-3 py-3 text-left font-semibold text-slate-700">Image</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Product Name / Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Country of Manufacturer</th>
                <th className="w-12 px-3 py-3 text-center font-semibold text-slate-700">QR</th>
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Status</th>
                {canManageProject && (
                  <th className="w-36 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => {
                const isSelected = selectedIds.has(p.id);
                return (
                  <tr
                    key={p.id}
                    className={`border-b transition-colors ${isSelected ? 'bg-orange-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                    data-testid={`ab-product-row-${p.id}`}
                  >
                    {canManageProject && (
                      <td className="w-10 px-3 py-2">
                        <OrangeCheckbox
                          id={`ab-select-row-${p.id}`}
                          checked={isSelected}
                          onChange={() => toggleSelectRow(p.id)}
                          testId={`ab-select-row-${p.id}`}
                        />
                      </td>
                    )}
                    <td className="w-16 px-3 py-2">
                      {p.imageUrl || p.imageCloudinary?.urls?.thumbnail ? (
                        <img
                          src={p.imageCloudinary?.urls?.thumbnail || p.imageUrl}
                          alt={p.productNameModel}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-200 flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-slate-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{p.manufacturerName || '-'}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{p.productNameModel}</td>
                    <td className="px-3 py-2 text-slate-600">{p.countryCode ? getCountryName(p.countryCode) : '-'}</td>
                    <td className="w-12 px-3 py-2 text-center" data-testid={`ab-qr-cell-${p.id}`}>
                      <button
                        type="button"
                        className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                        onClick={() => setQrModal({ open: true, qrToken: p.qrToken, productName: p.productNameModel })}
                        data-testid={`ab-qr-thumbnail-${p.id}`}
                        title="Click to enlarge QR code"
                      >
                        <QRThumbnail qrToken={p.qrToken} size={32} />
                      </button>
                    </td>
                    <td className="w-20 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        p.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'
                      }`} data-testid={`ab-product-status-${p.id}`}>
                        {p.status || 'draft'}
                      </span>
                    </td>
                    {canManageProject && (
                      <td className="w-36 px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handlePrint(p)} disabled={p.status !== 'active'} data-testid={`ab-print-product-${p.id}`} title={p.status === 'active' ? 'Preview' : 'Only active products can be previewed'}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handleListDownloadPdf(p)} disabled={p.status !== 'active' || pdfLoading === p.id} data-testid={`ab-download-pdf-${p.id}`} title="Download PDF">
                            {pdfLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => handleEdit(p)} data-testid={`ab-edit-product-${p.id}`} title="Edit">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => handleDelete(p)} disabled={deleting === p.id} data-testid={`ab-delete-product-${p.id}`} title="Delete">
                            {deleting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showWizard && (
        <AddAirBarrierProductWizard
          open={showWizard}
          onOpenChange={(open) => { if (!open) handleWizardClose(); }}
          onComplete={handleWizardComplete}
          onRefresh={fetchProducts}
          editProduct={editingProduct}
          companyId={companyId}
          projectId={projectId}
          csiItemId={csiItemId}
        />
      )}

      {csiItem && (
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-2xl" data-testid="ab-share-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Share This Specification
              </DialogTitle>
              <DialogDescription>
                Share {csiItem.itemTitle} specification with a sub-contractor company
              </DialogDescription>
            </DialogHeader>
            <ShareSection
              asModal
              tabValue=""
              inputIdPrefix="air-barrier"
              csiItem={csiItem}
              project={project}
              user={user}
              shareCompanySearch={shareCompanySearch}
              showCompanySuggestions={showCompanySuggestions}
              companySuggestions={companySuggestions}
              highlightedIndex={highlightedIndex}
              searchingCompanies={searchingCompanies}
              handleSearchCompanies={handleSearchCompanies}
              handleCompanySearchKeyDown={handleCompanySearchKeyDown}
              setShowCompanySuggestions={setShowCompanySuggestions}
              setHighlightedIndex={setHighlightedIndex}
              handleSelectCompany={handleSelectCompany}
              handleUnshareCSI={handleUnshareCSI}
            />
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!printProduct} onOpenChange={(open) => { if (!open) handlePrintClose(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="ab-print-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Preview
            </DialogTitle>
            <DialogDescription>Review before printing</DialogDescription>
          </DialogHeader>
          {printLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : printProduct ? (
            <>
              <div className="flex-1 overflow-y-auto border rounded-lg" id="ab-print-content-area">
                <AirBarrierPrintView product={printProduct} project={printProduct.project} user={printProduct.viewedBy} showActions onPreviewSubmittal={(s) => {
                  axios.get(`${API}/air-barrier-products/${printProduct.projectId}/${printProduct.id}/submittals/${s.id}/preview-url`).then(res => {
                    setPreviewSubmittal({ ...s, cloudinaryUrl: res.data?.url });
                  }).catch(() => toast.error('Failed to load preview'));
                }} onDownloadSubmittal={(sId, fName) => {
                  axios.get(`${API}/air-barrier-products/${printProduct.projectId}/${printProduct.id}/submittals/${sId}/download-url`).then(res => {
                    const link = document.createElement('a'); link.href = res.data.url; link.download = fName || 'document'; link.click();
                  }).catch(() => toast.error('Failed to download'));
                }} />
              </div>
              <div className="flex justify-end gap-2 pt-3 shrink-0">
                <Button variant="outline" size="sm" onClick={() => setPrintProduct(null)}>Close</Button>
                <Button variant="outline" size="sm" onClick={handleModalDownloadPdf} data-testid="ab-download-pdf-btn">
                  <Download className="h-4 w-4 mr-2" />Download
                </Button>
                <Button size="sm" onClick={triggerBrowserPrint} data-testid="ab-print-confirm-btn">
                  <Printer className="h-4 w-4 mr-2" />Print
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        {...confirmState}
        onClose={() => setConfirmState({ open: false })}
      />

      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, productName: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.productName}
      />
      <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />
      {pdfProduct && (
        <div style={{position:'fixed',left:'-10000px',top:'0px',width:'794px',background:'#fff',zIndex:-1,overflow:'visible',pointerEvents:'none'}} ref={pdfRef}>
          <AirBarrierPrintView product={pdfProduct} project={pdfProduct.project} user={pdfProduct.viewedBy} />
        </div>
      )}
    </div>
  );
};

export default AirBarriersSection;
